import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LiveChat } from './components/LiveChat';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { PageTransition } from './components/PageTransition';
// Home is the landing route — keep it eager so the first paint is instant.
import { Home } from './pages/Home';
import { CartProvider } from './components/CartContext';
import { CartDrawer } from './components/CartDrawer';
import { AnnouncementBar } from './components/AnnouncementBar';
import { I18nProvider } from './utils/i18n';
// Every other route is code-split and fetched on demand, so they no longer
// weigh down the initial bundle. Named exports are mapped to a default for lazy.
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Product = lazy(() => import('./pages/Product').then(m => ({
  default: m.Product
})));
const Project = lazy(() => import('./pages/Project').then(m => ({
  default: m.Project
})));
const HelpMeChoose = lazy(() => import('./pages/HelpMeChoose').then(m => ({
  default: m.HelpMeChoose
})));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({
  default: m.AboutPage
})));
const DeliveryZones = lazy(() => import('./pages/DeliveryZones').then(m => ({
  default: m.DeliveryZones
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const DeliveryPrep = lazy(() => import('./pages/DeliveryPrep').then(m => ({
  default: m.DeliveryPrep
})));
const CompareMaterials = lazy(() => import('./pages/CompareMaterials').then(m => ({
  default: m.CompareMaterials
})));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({
  default: m.NotFound
})));
const TrackOrder = lazy(() => import('./pages/TrackOrder').then(m => ({
  default: m.TrackOrder
})));
const TrackOrderLookup = lazy(() => import('./pages/TrackOrderLookup').then(m => ({
  default: m.TrackOrderLookup
})));
const Calculator = lazy(() => import('./pages/Calculator').then(m => ({
  default: m.Calculator
})));
const CityMaterial = lazy(() => import('./pages/CityMaterial').then(m => ({
  default: m.CityMaterial
})));
const County = lazy(() => import('./pages/County').then(m => ({
  default: m.County
})));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas').then(m => ({
  default: m.ServiceAreas
})));
const LogoConcepts = lazy(() => import('./components/LogoConcepts').then(m => ({
  default: m.LogoConcepts
})));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({
  default: m.RefundPolicy
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const DailyTicketForm = lazy(() => import('./pages/DailyTicketForm').then(m => ({
  default: m.DailyTicketForm
})));
function RouteFallback() {
  return <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf7] dark:bg-[#222222]" role="status" aria-label="Loading" data-id="element-0" data-mp="13ey4q06k1ic2">
      <span className="h-8 w-8 rounded-full border-2 border-brand-orange border-t-transparent animate-spin" data-id="element-1" data-mp="1w3c68pwuqwsd" />
    </div>;
}
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Two-pass scroll: first attempt accounts for the sticky navbar height,
      // then a follow-up corrects for late-rendering content (Services, etc.)
      // that can push the target further down the page.
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const navHeight = document.querySelector('nav')?.getBoundingClientRect().height || 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      };
      const t1 = setTimeout(scrollToTarget, 120);
      const t2 = setTimeout(scrollToTarget, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-2" data-mp="vx080466rhwl">
      <Suspense fallback={<RouteFallback data-id="element-4" data-mp="vybdq9j6yzqc" />} data-id="element-3" data-mp="vvp6yc44ko96">
        <Routes location={location} key={location.pathname} data-id="element-5" data-mp="3knt2p3oxszqi">
          <Route path="/" element={<PageTransition data-id="element-7" data-mp="3gmh8slz15sci">
                <Home data-id="element-8" data-mp="3gm7pfh8hqjfs" />
              </PageTransition>} data-id="element-6" data-mp="3kpdskg0b3ui1" />
          <Route path="/contact" element={<PageTransition data-id="element-10" data-mp="1w4yk20vebtzi">
                <Contact data-id="element-11" data-mp="1w5q36c7t3z8w" />
              </PageTransition>} data-id="element-9" data-mp="3gotzpomrnd1p" />
          <Route path="/shop/:slug" element={<PageTransition data-id="element-13" data-mp="206tld5dbu7da">
                <Product data-id="element-14" data-mp="203xnfh1hbiou" />
              </PageTransition>} data-id="element-12" data-mp="205qugxg084xd" />
          <Route path="/projects/:slug" element={<PageTransition data-id="element-16" data-mp="2530h65c0e2hi">
                <Project data-id="element-17" data-mp="29vjvj4iit1nd" />
              </PageTransition>} data-id="element-15" data-mp="25425vcly6nhp" />
          <Route path="/help" element={<PageTransition data-id="element-19" data-mp="2euwsii33vp3o">
                <HelpMeChoose data-id="element-20" data-mp="2evpr2vrwv8aq" />
              </PageTransition>} data-id="element-18" data-mp="29x4pot0xaikp" />
          <Route path="/about" element={<PageTransition data-id="element-22" data-mp="2jthrke6mg2qj">
                <AboutPage data-id="element-23" data-mp="2jsomrir83nb1" />
              </PageTransition>} data-id="element-21" data-mp="2ex95egu0b99v" />
          <Route path="/delivery-zones" element={<PageTransition data-id="element-25" data-mp="2nww7l4uk8j3z">
                <DeliveryZones data-id="element-26" data-mp="2ntrybt28mx6g" />
              </PageTransition>} data-id="element-24" data-mp="2nvup4fb7suc8" />
          <Route path="/subcontractors" element={<Navigate to="/" replace data-id="element-28" data-mp="2ssbknukquazc" />} data-id="element-27" data-mp="2stedw95jgpif" />
          <Route path="/contractor-ordering" element={<Navigate to="/" replace data-id="element-30" data-mp="tjob78qxrzg8" />} data-id="element-29" data-mp="tida5govl5st" />
          <Route path="/diy-help" element={<Navigate to="/blog/perfect-gravel-driveway" replace data-id="element-32" data-mp="yhg3w4zenplh" />} data-id="element-31" data-mp="yg4xryihm5r7" />
          <Route path="/blog" element={<PageTransition data-id="element-34" data-mp="21sdfy6d1lvia">
                <Blog data-id="element-35" data-mp="21tojq66hn39e" />
              </PageTransition>} data-id="element-33" data-mp="21r2c66jlknr6" />
          <Route path="/blog/:slug" element={<PageTransition data-id="element-37" data-mp="1wubuxfw23m46">
                <BlogPost data-id="element-38" data-mp="2as0hcs5rv1bc" />
              </PageTransition>} data-id="element-36" data-mp="1wtiyp8uq4f0a" />
          <Route path="/delivery-prep" element={<PageTransition data-id="element-40" data-mp="25t5pl0ptu2f3">
                <DeliveryPrep data-id="element-41" data-mp="25s4ckqp935uq" />
              </PageTransition>} data-id="element-39" data-mp="2attmg30qldh8" />
          <Route path="/compare" element={<PageTransition data-id="element-43" data-mp="2kpmxtj2pz0xf">
                <CompareMaterials data-id="element-44" data-mp="2kovbkyv0ifjt" />
              </PageTransition>} data-id="element-42" data-mp="2kqga0zkg6imy" />
          <Route path="/calculator" element={<PageTransition data-id="element-46" data-mp="2fr3bhhk7rn4j">
                <Calculator data-id="element-47" data-mp="2tiqjufrfkm3o" />
              </PageTransition>} data-id="element-45" data-mp="2fs4xui6ojw1k" />
          {/* "Why shop local" is now the second half of the About page, so the
              old standalone URL redirects there rather than 404ing. */}
          <Route path="/local-vs-national" element={<Navigate to="/about" replace data-id="element-49" data-mp="33hph726qwvbd" />} data-id="element-48" data-mp="33go64xpqc8tn" />
          <Route path="/track" element={<PageTransition data-id="element-51" data-mp="2yimfnrpz5785">
                <TrackOrderLookup data-id="element-52" data-mp="2yje7crggakt5" />
              </PageTransition>} data-id="element-50" data-mp="2yhuvdd1tuvx6" />
          <Route path="/track/:orderId" element={<PageTransition data-id="element-54" data-mp="3i4efj0p9h2g6">
                <TrackOrder data-id="element-55" data-mp="3n2pufpuey359" />
              </PageTransition>} data-id="element-53" data-mp="3i3d6f1rt2q13" />
          <Route path="/service-areas" element={<PageTransition data-id="element-57" data-mp="385gw2c6ozz41">
                <ServiceAreas data-id="element-58" data-mp="384f8574kvi4w" />
              </PageTransition>} data-id="element-56" data-mp="3n27ou2lv2cko" />
          <Route path="/logo-concepts" element={<PageTransition data-id="element-60" data-mp="3d3iecuwanueg">
                <LogoConcepts data-id="element-61" data-mp="3d2p5nodryuto" />
              </PageTransition>} data-id="element-59" data-mp="3d4jqz3sykoya" />
          <Route path="/refund-policy" element={<PageTransition data-id="element-63" data-mp="2zbu4yzdfjk16">
                <RefundPolicy data-id="element-64" data-mp="343vewen4kyo1" />
              </PageTransition>} data-id="element-62" data-mp="2zcvcwuz5fqeo" />
          <Route path="/privacy-policy" element={<PageTransition data-id="element-66" data-mp="2pf7k4al3ssz5">
                <PrivacyPolicy data-id="element-67" data-mp="2pg0jgqhqgg79" />
              </PageTransition>} data-id="element-65" data-mp="3456fk5l9xqax" />
          <Route path="/terms-of-service" element={<PageTransition data-id="element-69" data-mp="2uf3ls36bw8bj">
                <TermsOfService data-id="element-70" data-mp="2ubxymth9fgig" />
              </PageTransition>} data-id="element-68" data-mp="2ue22jbf5sfiq" />
          <Route path="/form" element={<PageTransition data-id="element-72" data-mp="2gf2mgs5pp8y6">
                <DailyTicketForm data-id="element-73" data-mp="2kfuvbkao9btx" />
              </PageTransition>} data-id="element-71" data-mp="2gfc49sgls9ss" />

          {/* County-level landing pages. Distinct from the two-segment
              city × material route below — React Router matches on segment
              count, so the two coexist. County.tsx renders the 404 page for
              any slug outside SERVICE_COUNTIES. */}
          <Route path="/gravel-delivery/:countySlug" element={<PageTransition data-id="element-75" data-mp="2ly4o12j6t76j">
                <County data-id="element-76" data-mp="2lywclteda4nb" />
              </PageTransition>} data-id="element-74" data-mp="2qvwfjvfx6ra7" />
          <Route path="/gravel-delivery/:citySlug/:materialSlug" element={<PageTransition data-id="element-78" data-mp="2hxayk4q3jkbj">
                <CityMaterial data-id="element-79" data-mp="2hycdwle5asz2" />
              </PageTransition>} data-id="element-77" data-mp="2m0h2h5pqkzeu" />
          <Route path="*" element={<PageTransition data-id="element-81" data-mp="2d3pzsagoimba">
                <NotFound data-id="element-82" data-mp="286gdv4si0ss7" />
              </PageTransition>} data-id="element-80" data-mp="2d514cchy7y3g" />
        </Routes>
      </Suspense>
    </AnimatePresence>;
}
function AppContent({
  isDark,
  toggleDark
}: {
  isDark: boolean;
  toggleDark: () => void;
}) {
  const location = useLocation();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  // The daily ticket form (/form) is a standalone operational document. It
  // renders bare — no site chrome (navbar, footer, announcement bar, live
  // chat, sticky CTA, cart) — so it prints clean and stays out of the way.
  const isStandalonePage = location.pathname === '/form';
  if (isStandalonePage) {
    // The daily ticket is a printed white document — never apply the `dark`
    // class here so it always renders on a clean white surface regardless of
    // the visitor's theme.
    return <div className="min-h-screen font-sans" data-id="element-83" data-mp="3m484mqzi28r4">
        <ScrollToHash data-id="element-84" data-mp="3r4uorypegjza" />
        <AnimatedRoutes data-id="element-85" data-mp="3r65x8btsifwq" />
      </div>;
  }
  return <div className={`min-h-screen flex flex-col font-sans ${isDark ? 'dark' : ''} ${isContractorPage ? 'contractor-theme' : ''}`} data-id="element-86" data-mp="3w4h5ii4fsicu">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-brand-orange focus:text-white focus:rounded-lg focus:font-bold focus:outline-none focus:ring-2 focus:ring-white" data-id="element-87" data-mp="4q308hvosdlb">
        Skip to main content
      </a>
      <div className="flex-grow bg-[#fdfbf7] dark:bg-[#222222] transition-colors duration-300 pb-16 md:pb-0" id="main-content" data-id="element-88" data-mp="2f63k5qpijgcw">
        <ScrollToHash data-id="element-89" data-mp="2k4dg43j2akzk" />
        <AnnouncementBar data-id="element-90" data-mp="2k81d5cnptpj1" />
        <Navbar isDark={isDark} toggleDark={toggleDark} data-id="element-91" data-mp="2p4rr9ufymb1t" />
        <AnimatedRoutes data-id="element-92" data-mp="2p3889w2x5lwc" />
        <Footer data-id="element-93" data-mp="2u4e628bfrzcq" />
        <LiveChat data-id="element-94" data-mp="2u4xjbbq55zkf" />
        <StickyMobileCTA data-id="element-95" data-mp="2u3cnzjw06cli" />
        <ScrollToTopButton data-id="element-96" data-mp="2u3mh3gccgzvf" />
        <CartDrawer data-id="element-97" data-mp="1wgnl77th7vyl" />
      </div>
    </div>;
}
// Decide the initial theme for a first-time visitor (no saved preference yet):
//   1. An explicit saved choice always wins — a returning visitor who picked a
//      theme keeps it.
//   2. Everyone else starts in LIGHT mode.
//
// Light is the deliberate default. Customers are contractors and homeowners
// pricing material outdoors on a phone in daylight, so light mode is the
// higher-value surface and the one held to the tightest design standard. The
// previous behavior deferred to the OS `prefers-color-scheme` and then to a
// time-of-day clock rule, which meant a majority of first-time visitors landed
// in dark mode by accident — and made the very first impression the mode that
// gets less design attention. The toggle still switches modes and still
// persists, so nothing is taken away from anyone who prefers dark.
function getInitialTheme(): boolean {
  try {
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
  } catch {
    // localStorage can throw in private mode / embedded webviews / when
    // storage is blocked. Fall through to the light default.
  }
  return false;
}
export function App() {
  const [isDark, setIsDark] = useState(getInitialTheme);
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch {}
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);
  const toggleDark = () => setIsDark(!isDark);
  return <I18nProvider data-id="element-98" data-mp="2fnpqmjkoz5ub">
      <Router data-id="element-99" data-mp="2fn6jlxwky1v2">
        <CartProvider data-id="element-100" data-mp="2fmoh4jjbqrep">
          <AppContent isDark={isDark} toggleDark={toggleDark} data-id="element-101" data-mp="2fm4xmye0zuyk" />
        </CartProvider>
      </Router>
    </I18nProvider>;
}